import { AttachmentKind } from './attachment-badge.types';

/*
  Which family a file belongs to, and so which tint it wears. **Keyed on the extension rather than a
  MIME type**, because the extension is the thing that is always there: browsers report no type at
  all for some files and `application/octet-stream` for plenty of others, and a file named on the
  wire has no type in the first place.
*/
const KINDS: Record<string, AttachmentKind> = {
  CSV: AttachmentKind.TEXT,
  DOC: AttachmentKind.DOCUMENT,
  DOCX: AttachmentKind.DOCUMENT,
  GIF: AttachmentKind.IMAGE,
  HEIC: AttachmentKind.IMAGE,
  HEIF: AttachmentKind.IMAGE,
  JPEG: AttachmentKind.IMAGE,
  JPG: AttachmentKind.IMAGE,
  JSON: AttachmentKind.TEXT,
  MD: AttachmentKind.TEXT,
  PDF: AttachmentKind.PDF,
  PNG: AttachmentKind.IMAGE,
  RTF: AttachmentKind.TEXT,
  TIF: AttachmentKind.IMAGE,
  TIFF: AttachmentKind.IMAGE,
  TXT: AttachmentKind.TEXT,
  WEBP: AttachmentKind.IMAGE,
};

/*
  The words for a family, for the line under a file where no picture can be drawn. They name the
  kind rather than repeating the extension, which the badge beside them is already showing - and
  `image` is here for the pictures a browser cannot decode, which get a badge like a document does.
*/
const LABELS: Record<AttachmentKind, string> = {
  [AttachmentKind.DOCUMENT]: 'Document',
  [AttachmentKind.IMAGE]: 'Image',
  [AttachmentKind.OTHER]: 'File',
  [AttachmentKind.PDF]: 'PDF',
  [AttachmentKind.TEXT]: 'Text file',
};

/**
 * AttachmentBadgeUtil - I read a file name for the family it belongs to and the words for it, so
 * callers saying something else about the same file say it the same way. ONLY concerned with
 * reading the name - no rendering, no fetching.
 *
 * A name is all it takes, so a bare extension works too: `kindOf({ fileName: '.pdf' })`.
 */
export const AttachmentBadgeUtil = {
  extensionOf({ fileName }: { fileName: string }) {
    const index = fileName.lastIndexOf('.');

    return index === -1 ? '' : fileName.slice(index + 1).toUpperCase();
  },

  kindOf({ fileName }: { fileName: string }) {
    return (
      KINDS[AttachmentBadgeUtil.extensionOf({ fileName })] ??
      AttachmentKind.OTHER
    );
  },

  labelOf({ fileName }: { fileName: string }) {
    return LABELS[AttachmentBadgeUtil.kindOf({ fileName })];
  },
};
